import { Loader2 } from "lucide-react";
import React, { useState } from "react";

import {
  Alert,
  AlertDescription,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  useCreatePaymentMethod,
  useStripeCreatePaymentMethod,
} from "@/hooks/payment";
import { UserPath } from "@generated/proto/model";
import {
  CardElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

interface PaymentMethodFormProps {
  userPath: UserPath;
  onSuccess?: (paymentMethodId: string) => void;
  onCancel?: () => void;
  setAsDefault?: boolean;
}

export function PaymentMethodForm({
  userPath,
  onSuccess,
  onCancel,
  setAsDefault = false,
}: PaymentMethodFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { createPaymentMethod: stripeCreatePaymentMethod } = useStripeCreatePaymentMethod();
  const { createPaymentMethod, loading: isCreatingPaymentMethod } = useCreatePaymentMethod();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create payment method with Stripe
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message || 'Failed to create payment method');
        return;
      }

      if (!paymentMethod) {
        setError('Failed to create payment method');
        return;
      }

      // Save payment method to backend
      const response = await createPaymentMethod(
        paymentMethod.id,
        setAsDefault
      );

      onSuccess?.(response?.paymentMethod?.paymentMethodId || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className="p-4 border rounded-md bg-gray-50">
        <CardElement options={cardElementOptions} />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        )}

        <Button
          type="submit"
          disabled={!stripe || isLoading}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {setAsDefault ? 'Add & Set as Default' : 'Add Payment Method'}
        </Button>
      </div>
    </form>
  );
}